import Vue from 'vue'
import Router from 'vue-router'
import {mainStore} from '../stores/index.js';
// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer');

// Views
const Dashboard = () => import('@/views/Dashboard');

const CustomerList = () => import('@/views/customers/CustomerList');
const CustomerModules = () => import('@/views/customers/CustomerModules');
const CreateClient = () => import('@/views/customers/CreateClient');
const CreateProject = () => import('@/views/customers/CreateProject');
const CreateOffice = () => import('@/views/customers/CreateOffice');
const CompanyGroupList = () => import("@/views/customers/CompanyGroupList");
const CompanyList = () => import("@/views/customers/CompanyList");
const EditCompany = () => import("@/views/customers/EditCompany");

const BillingSetup = () => import('@/views/billings/Setup');
const Invoice = () => import('@/views/billings/Invoice');
const Payment = () => import('@/views/billings/Payment');
const DueStatus = () => import('@/views/billings/DueStatus');

const PayDashboard = () => import('@/views/pay/PayDashboard');
const PayClients = () => import('@/views/pay/PayClients');
const PayItems = () => import('@/views/pay/PayItems');

const Version = () => import('@/views/settings/Version');
const Module = () => import('@/views/settings/Module');
const VersionConfigurator = () => import('@/views/settings/VersionConfigurator')
// Views - Pages
const Page404 = () => import('@/views/pages/Page404');
const Page500 = () => import('@/views/pages/Page500');
const Login = () => import('@/views/pages/Login');

//Views - Resellers
const ResellerList = () => import('@/views/resellers/ResellerList');
const ViewReseller = () => import('@/views/resellers/ViewReseller');

//Views - Orders
const PurchaseOrderList = () => import('@/views/orders/PurchaceOrderList');
const CreatePurchaseOrder = () => import('@/views/orders/CreatePurchaseOrder');
const CreateDeliveryReceipt = () => import("@/views/orders/CreateDeliveryReceipt");
const ViewDeliveryDetails = () => import("@/views/orders/ViewDeliveryDetails");
const EditPurchaseOrder = () => import("@/views/orders/editPurchaseOrder");

// Users
const Users = () => import('@/views/users/Users');
const User = () => import('@/views/users/User');

// Customer Support
const PasswordReset = () => import('@/views/customerSupport/PasswordReset');
const SupportManagement = () => import("@/views/customerSupport/SupportManagement");
const CloneTemporaryAllocations = () => import("@/views/customerSupport/CloneTemporaryAllocations");
const ArchivedReportDownload = () => import("@/views/customerSupport/ArchivedReportDownload")
//Payroll
const PayrollManager = () => import('@/views/payroll/PayrollManager');

// ProvidentFund
const ProvidentFund = () => import('@/views/payroll/ProvidentFund');

//General Settings
const MobilePunchGeneralSettings = () => import("@/views/generalSettings/MobilePunchGeneralSettings")
const FiscalYearGeneralSettings = () => import("@/views/generalSettings/FiscalYearGeneralSettings")

const ReportSettingsGeneralSettings = () => import("@/views/generalSettings/ReportSettingsGeneralSettings")
const AttendanceSettings = () => import("@/views/generalSettings/AttendanceSettings")

const ParkingManagementGeneralSettings = () => import("@/views/generalSettings/ParkingManagementGeneralSettings")

const MembershipManagementGeneralSettings = () => import("@/views/generalSettings/MembershipManagementGeneralSettings")

const SiteManagementGeneralSettings = () => import("@/views/generalSettings/SiteManagementGeneralSettings")

const LineManagerSettings = ()=>import("@/views/generalSettings/LineManagerSettings.vue")

const OvertimeGeneralSettings = () => import("@/views/generalSettings/OvertimeGeneralSettings");

const LocationTrackingGeneralSettings = () => import("@/views/generalSettings/LocationTrackingGeneralSettings");

const EmployeeDeviceLimitsSettings = () => import("@/views/generalSettings/EmployeeDeviceLimitsSettings");

const SubscriptionRequestList = () => import("@/views/subscription/SubscriptionRequestList");

// Application Logs
const LogExplorer = () => import("@/views/logs/LogExplorer");
const LogSourceSettings = () => import("@/views/logs/LogSourceSettings");
const LogAccessAudit = () => import("@/views/logs/LogAccessAudit");

Vue.use(Router);

function guard(to, from, next) {
	if (to.name === "Login") {
		next();
		return;
	}

	if (!mainStore.getters.loggedIn) {
		next({ name: "Login" });
		return; // Fixed bug where execution continued after redirect
	}

	// Role-Based Access Control
	const subscriptionOnlyRoutes = ['Subscription Request List', 'Subscription', 'Login', 'Page404', 'Page500'];
	// Application Logs is ROLE_ADMIN only. The backend restricts /logs/** to ROLE_ADMIN because the
	// files carry request URIs, client IPs and employee ids, so a READONLY user reaching these
	// routes would land on a page of 403s. There was no admin-only gate in this panel before.
	const adminOnlyRoutes = ['Application Logs', 'Log Explorer', 'Log Sources', 'Log Access Audit'];
	try {
		const role = localStorage.getItem('role');

		if (role) {
			// ROLE_SUPPORT is restricted strictly to Subscription flows.
			if (role === 'ROLE_SUPPORT') {
				if (!subscriptionOnlyRoutes.includes(to.name)) {
					next({ name: 'Subscription Request List' });
					return;
				}
			}
			if (role !== 'ROLE_ADMIN' && adminOnlyRoutes.includes(to.name)) {
				next({ name: 'Dashboard' });
				return;
			}
			// ROLE_ADMIN and ROLE_READONLY may view every other page.
			// Readonly is prevented from mutating via hidden write controls (UI) and backend 403s.
			next();
			return;
		}

		// Fallback for legacy sessions that have no stored role:
		// preserve the previous behaviour (users with id > 1 are limited to Subscription flows).
		const token = localStorage.getItem('token');
		if (token && token.split('.').length === 3) {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			}).join(''));
			const payload = JSON.parse(jsonPayload);

			if (payload && Number(payload.sub) > 1) {
				if (!subscriptionOnlyRoutes.includes(to.name)) {
					next({ name: 'Subscription Request List' });
					return;
				}
			}
		}
	} catch (error) {
		console.error("Token decoding error in router guard", error);
		next({ name: "Login" });
		return;
	}

	next();
}

const router = new Router({
	mode: 'history', // https://router.vuejs.org/api/#mode
	linkActiveClass: 'open active',
	scrollBehavior: () => ({y: 0}),
	routes: [
		{
			path: '/',
			redirect: '/dashboard',
			name: 'Home',
			component: DefaultContainer,
			children: [
				{
					path: 'dashboard',
					name: 'Dashboard',
					component: Dashboard
				},
				{
					path: 'analytics',
					redirect: '/analytics/dashboard',
					component: { render(c) { return c('router-view') } },
					children: [
						{
							path: 'dashboard',
							name: 'GAD Analytics Dashboard',
							component: () => import('@/views/analytics/AnalyticsDashboard')
						},
						{
							path: 'report',
							name: 'GAD Analytics Report',
							component: () => import('@/views/analytics/AnalyticsReport')
						},
						{
							path: 'systems',
							name: 'GAD Analytics Systems',
							component: () => import('@/views/analytics/AnalyticsSystems')
						}
					]
				},
				{
					path: 'issue-tracker',
					redirect: '/issue-tracker/dashboard',
					component: { render(c) { return c('router-view') } },
					children: [
						{
							path: 'dashboard',
							name: 'Issue Tracker Dashboard',
							component: () => import('@/views/issueTracker/IssueTrackerDashboard')
						},
						{
							path: 'list',
							name: 'Issue List',
							component: () => import('@/views/issueTracker/IssueList')
						},
						{
							path: 'create',
							name: 'Create Issue',
							component: () => import('@/views/issueTracker/IssueCreate')
						},
						{
							path: 'team-members',
							name: 'Team Members',
							component: () => import('@/views/issueTracker/TeamMembers')
						},
						{
							path: 'systems',
							name: 'Systems',
							component: () => import('@/views/issueTracker/IssueSystems')
						},
						{
							path: ':id',
							name: 'Issue Detail',
							component: () => import('@/views/issueTracker/IssueDetail')
						}
					]
				},
				{
					path: 'monitoring',
					redirect: '/monitoring/dashboard',
					component: { render(c) { return c('router-view') } },
					children: [
						{
							path: 'dashboard',
							name: 'Monitoring Dashboard',
							component: () => import('@/views/monitoring/MonitoringDashboard')
						},
						{
							path: 'live',
							name: 'Live Monitoring',
							component: () => import('@/views/monitoring/LiveMonitoring')
						},
						{
							path: 'reports',
							name: 'Downtime Reports',
							component: () => import('@/views/monitoring/DowntimeReports')
						},
						{
							path: 'downtime-entry',
							name: 'Downtime Entry',
							component: () => import('@/views/monitoring/DowntimeEntry')
						},
						{
							path: 'systems',
							name: 'Monitoring Systems',
							component: () => import('@/views/monitoring/MonitoringSystems')
						}
					]
				},
				{
					path: 'customers',
					redirect: '/customers/list',
					name: 'Customer',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'list',
							name: 'List',
							component: CustomerList
						},
						{
							path: 'modules',
							name: 'Modules',
							component: CustomerModules
						},
						{
							path: 'create-client',
							name: 'Create Client',
							component: CreateClient
						},
						{
							path: 'create-project',
							name: 'Create project',
							component: CreateProject
						},
						{
							path: 'create-office',
							name: 'Create Office',
							component: CreateOffice
						},
						{
							path: 'company-groups',
							name: 'Group of Companies',
							component: CompanyGroupList
						},
						{
							path: 'companies',
							name: 'Companies',
							component: CompanyList
						},
						{
							path: 'companies/:companyId',
							name: 'Edit Company',
							component: EditCompany
						},


					]
				},
				{
					path: 'customer-support',
					redirect: '/customer-support/password-reset',
					name: 'Support-Management',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'password-reset',
							name: 'Password Reset',
							component: PasswordReset
						},
						{
							path: 'support-management',
							name: 'Customer Support Management',
							component: SupportManagement
						},
						{
							path: 'temporary-allocations/clone',
							name: 'Clone Temporary Allocations',
							component: CloneTemporaryAllocations
						},
						{
							path: 'report-archive',
							name: 'Report Archive',
							component: ArchivedReportDownload
						}
					]
				},
				{
					path: 'payroll',
					redirect: '/payroll/payroll-manager',
					name: 'Payroll',
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: 'payroll-manager',
							name: 'Payroll Manager',
							component: PayrollManager
						},
						{
							path: 'provident-fund',
							name: 'Provident Fund',
							component: ProvidentFund
						},

					]
				},
				// Tipsoi Pay.
				//
				// NOTE the path: this section is routed at /tipsoi-pay, NOT /pay, even though /pay is
				// the URL people ask for. vue-router matches router-link-active by inclusive string
				// prefix using a raw indexOf, so "/payroll/payroll-manager".indexOf("/pay") === 0 is
				// true — and _custom.scss keys the sidebar's expanded state off that class. A parent
				// at /pay would leave this menu hanging open on every Payroll page. /pay is kept as a
				// redirect below so the short URL still works.
				//
				// The legacy Billings pages live on underneath as read-only history: their three
				// tables were empty everywhere we could measure, but production has not been
				// confirmed, so they are relocated rather than deleted.
				{
					path: 'tipsoi-pay',
					redirect: '/tipsoi-pay/dashboard',
					name: 'Tipsoi Pay',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'dashboard',
							name: 'Tipsoi Pay Dashboard',
							component: PayDashboard
						},
						{
							path: 'clients',
							name: 'Tipsoi Pay Clients',
							component: PayClients
						},
						{
							path: 'items',
							name: 'Tipsoi Pay Items',
							component: PayItems
						},
						{
							path: 'legacy/setup',
							name: 'Billing Setup',
							component: BillingSetup
						},
						{
							path: 'legacy/invoice',
							name: 'Invoice',
							component: Invoice
						},
						{
							path: 'legacy/payment',
							name: 'Payment',
							component: Payment
						},
						{
							path: 'legacy/due-status',
							name: 'Due Status',
							component: DueStatus
						}
					]
				},

				// Old URLs keep working. /pay is the short link people are given; the rest are the
				// bookmarks the Billings section leaves behind. A redirect route renders nothing and
				// so never matches router-link-active — /pay introduces no sidebar collision of its own.
				{path: 'pay', redirect: '/tipsoi-pay/dashboard'},
				{path: 'billings', redirect: '/tipsoi-pay/dashboard'},
				{path: 'billings/setup', redirect: '/tipsoi-pay/legacy/setup'},
				{path: 'billings/invoice', redirect: '/tipsoi-pay/legacy/invoice'},
				{path: 'billings/payment', redirect: '/tipsoi-pay/legacy/payment'},
				{path: 'billings/due-status', redirect: '/tipsoi-pay/legacy/due-status'},
				{
					path: 'settings',
					redirect: '/settings/version',
					name: 'Settings',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'version',
							name: 'Version',
							component: Version
						},
						{
							path: 'module',
							name: 'Module',
							component: Module
						},
						{
							path: 'configurator',
							name: 'VersionConfigurator',
							component: VersionConfigurator
						}
					]
				},
				{
					path: 'resellers',
					redirect: '/resellers/list',
					name: 'Reseller',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'list',
							name: 'Reseller List',
							component: ResellerList
						},
						{
							path: ':resellerId',
							name: 'Reseller Details',
							component: ViewReseller
						}
					]
				},
				{
					path: 'purchase-orders',
					redirect: '/purchase-orders/list',
					name: 'Purchase Order',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'list',
							name: 'P.O. List',
							component: PurchaseOrderList
						},
						{
							path: 'create',
							name: 'Create P.O.',
							component: CreatePurchaseOrder
						},
						{
							path: 'delivery-receipts/create',
							name: 'Create Delivery Receipt',
							component: CreateDeliveryReceipt
						},
						{
							path: ':purchaseOrderId',
							name: 'Delivery Details',
							component: ViewDeliveryDetails
						},
						{
							path: 'edit/:purchaseOrderId',
							name: 'Edit P.O.',
							component: EditPurchaseOrder
						}
					]
				},
				{
					path: 'users',
					meta: {label: 'Users'},
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: '',
							component: Users,
						},
						{
							path: ':id',
							meta: {label: 'User Details'},
							name: 'User',
							component: User,
						},
					]
				},
				{
					path: 'general-settings',
					redirect: '/general-settings/mobile-punch',
					name: 'GeneralSettings',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'mobile-punch',
							name: 'Mobile Punch',
							component: MobilePunchGeneralSettings
						},
						{
							path: 'overtime-settings',
							name: 'Overtime Settings',
							component: OvertimeGeneralSettings
						},
						{
							path: 'fiscal-year',
							name: 'Fiscal Year',
							component: FiscalYearGeneralSettings
						},
						{
							path: 'report-settings',
							name: 'Report Settings',
							component: ReportSettingsGeneralSettings
						},
						{
							path: 'parking-management',
							name: 'Parking Management',
							component: ParkingManagementGeneralSettings
						},
						{
							path: 'membership-management',
							name: 'Membership Management',
							component: MembershipManagementGeneralSettings
						},
						{
							path: 'site-management',
							name: 'Site Management',
							component: SiteManagementGeneralSettings
						},
						{
							path: 'attendance-settings',
							name: 'Attendance Settings',
							component: AttendanceSettings
						},
						{
							path: 'line-manager-settings',
							name: 'Line Manager Settings',
							component: LineManagerSettings
						},
						{
							path: 'location-tracking',
							name: 'Location Tracking',
							component: LocationTrackingGeneralSettings
						},
						{
							path: 'employee-device-limits',
							name: 'Employee and Device Limits',
							component: EmployeeDeviceLimitsSettings
						}
					]
				},
				{
					// '/application-logs', not '/logs': vue-router matches active links with an
					// inclusive indexOf, which is why '/pay' once matched '/payroll/*'. Nothing
					// currently starts with this prefix.
					path: 'application-logs',
					redirect: '/application-logs/explorer',
					name: 'Application Logs',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'explorer',
							name: 'Log Explorer',
							component: LogExplorer
						},
						{
							path: 'sources',
							name: 'Log Sources',
							component: LogSourceSettings
						},
						{
							path: 'audit',
							name: 'Log Access Audit',
							component: LogAccessAudit
						}
					]
				},
				{
					path: 'subscription',
					redirect: '/subscription/request-list',
					name: 'Subscription',
					component: {
						render(c) {
							return c('router-view')
						}
					},
					children: [
						{
							path: 'request-list',
							name: 'Subscription Request List',
							component: SubscriptionRequestList
						}
					]
				},
			]
		},
		{
			path: '/pages',
			redirect: '/pages/404',
			name: 'Pages',
			component: {
				render(c) {
					return c('router-view')
				}
			},
			children: [
				{
					path: '404',
					name: 'Page404',
					component: Page404
				},
				{
					path: '500',
					name: 'Page500',
					component: Page500
				},
			]
		},
		{
			path: '/login',
			name: 'Login',
			component: Login
		},
		{
			path: '*',
			redirect: mainStore.getters.loggedIn ? 'dashboard' : 'login'
		},

	]
});
router.beforeEach(guard);

export default router;
